const markdownListItemPattern = /^(\s*)(?:[-+*]|\d+\.)\s+/;
const fencePattern = /^\s*(```|~~~)/;
const legacyHtmlPattern = /<\/?(?:b|strong|br|p|ul|ol|li|i|em)(?:\s[^>]*)?\s*\/?>/i;
const legacyListPattern = /^(?:\s*)[•●▪◦‣–—]\s+|^(?:\s*)(?:\(\d+\)|\d+[)\]])\s+/m;

const isMarkdownListItem = (line: string) => markdownListItemPattern.test(line);

const convertHtmlList = (content: string, ordered: boolean) => {
  const items: string[] = [];
  let index = 0;

  content.replace(/<li(?:\s[^>]*)?>([\s\S]*?)<\/li>/gi, (_match, item) => {
    index += 1;
    const marker = ordered ? `${index}.` : "-";
    const normalizedItem = item
      .replace(/<br\s*\/?>/gi, " ")
      .replace(/\s+/g, " ")
      .trim();
    if (normalizedItem) items.push(`${marker} ${normalizedItem}`);
    return _match;
  });

  return items.length ? `\n${items.join("\n")}\n` : content;
};

/**
 * Converts a small, safe subset of HTML commonly returned by assistants to
 * Markdown. Unsupported HTML remains text and is escaped by ReactMarkdown.
 */
const normalizeSafeAssistantHtml = (text: string) => text
  .replace(/<ol(?:\s[^>]*)?>([\s\S]*?)<\/ol>/gi, (_match, content) => convertHtmlList(content, true))
  .replace(/<ul(?:\s[^>]*)?>([\s\S]*?)<\/ul>/gi, (_match, content) => convertHtmlList(content, false))
  .replace(/<br\s*\/?>\s*(?=<(?:b|strong)(?:\s[^>]*)?>)/gi, "\n\n")
  .replace(/<br\s*\/?>/gi, "  \n")
  .replace(/<p(?:\s[^>]*)?>/gi, "")
  .replace(/<\/p>/gi, "\n\n")
  .replace(/<(?:b|strong)(?:\s[^>]*)?>/gi, "**")
  .replace(/<\/(?:b|strong)>/gi, "**")
  .replace(/<(?:i|em)(?:\s[^>]*)?>/gi, "*")
  .replace(/<\/(?:i|em)>/gi, "*")
  .replace(/<\/?(?:ul|ol)(?:\s[^>]*)?>/gi, "\n")
  .replace(/<li(?:\s[^>]*)?>/gi, "\n- ")
  .replace(/<\/li>/gi, "")
  .replace(/&nbsp;/gi, " ");

const normalizeHtmlOutsideCodeFences = (text: string) => {
  const output: string[] = [];
  const plainText: string[] = [];
  let insideFence = false;

  const flushPlainText = () => {
    if (!plainText.length) return;
    output.push(normalizeSafeAssistantHtml(plainText.join("\n")));
    plainText.length = 0;
  };

  text.replace(/\r\n?/g, "\n").split("\n").forEach((line) => {
    if (fencePattern.test(line)) {
      flushPlainText();
      insideFence = !insideFence;
      output.push(line);
    } else if (insideFence) {
      output.push(line);
    } else {
      plainText.push(line);
    }
  });

  flushPlainText();
  return output.join("\n");
};

/**
 * Converts common list-like assistant output into Markdown list syntax.
 * Fenced code is left untouched so examples and source code are not changed.
 */
export function normalizeChatMarkdown(text: string): string {
  if (!legacyHtmlPattern.test(text) && !legacyListPattern.test(text)) {
    return text;
  }

  let insideFence = false;

  const normalizedLines = normalizeHtmlOutsideCodeFences(text).split("\n").map((line) => {
    if (fencePattern.test(line)) {
      insideFence = !insideFence;
      return line;
    }

    if (insideFence) return line;

    const bulletMatch = line.match(/^(\s*)[•●▪◦‣–—]\s+(.*)$/);
    if (bulletMatch) return `${bulletMatch[1]}- ${bulletMatch[2]}`;

    const numberedMatch = line.match(/^(\s*)(?:\((\d+)\)|(\d+)[)\]])\s+(.*)$/);
    if (numberedMatch) {
      const number = numberedMatch[2] || numberedMatch[3];
      return `${numberedMatch[1]}${number}. ${numberedMatch[4]}`;
    }

    return line;
  });

  const output: string[] = [];
  insideFence = false;

  normalizedLines.forEach((line) => {
    if (fencePattern.test(line)) {
      insideFence = !insideFence;
      output.push(line);
      return;
    }

    if (insideFence) {
      output.push(line);
      return;
    }

    const currentIsListItem = isMarkdownListItem(line);
    const previousLine = output[output.length - 1] || "";
    const previousIsListItem = isMarkdownListItem(previousLine);

    if (currentIsListItem && previousLine.trim() && !previousIsListItem) {
      output.push("");
    } else if (
      !currentIsListItem &&
      previousIsListItem &&
      line.trim() &&
      !/^\s+/.test(line)
    ) {
      output.push("");
    }

    output.push(line);
  });

  return output.join("\n");
}

